import type { ReactNode } from 'react'
import { Head } from '@inertiajs/react'
import DashboardLayout from '@/components/DashboardLayout'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import {
  Megaphone,
  FileText,
  DollarSign,
  Users,
  Heart,
  UserPlus,
  HelpCircle,
  Sparkles,
  Bell,
} from 'lucide-react'

type Stats = {
  published_campaigns: number
  draft_campaigns: number
  total_raised_cents: number
  total_supporters: number
  total_followers: number
}

type Donor = {
  id: string
  name: string | null
  amount_cents: number
  event_title: string | null
  created_at: string
}

type Follower = {
  id: string
  name: string
  created_at: string
}

type DashboardHomeProps = {
  greeting_name: string | null
  stats: Stats
  new_donors: { count: number; recent: Donor[] }
  new_followers: { count: number; recent: Follower[] }
}

function formatMoney(cents: number) {
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

function formatRelativeDate(iso: string) {
  const date = new Date(iso)
  const diffMs = Date.now() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 30) return `${diffDays}d ago`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

type StatCardProps = {
  label: string
  value: string | number
  icon: ReactNode
}

function StatCard({ label, value, icon }: StatCardProps) {
  return (
    <div className="rounded-xl border border-black/10 bg-card p-4 shadow-xs">
      <div className="flex items-center gap-2 text-muted-foreground">
        {icon}
        <span className="text-base font-medium">{label}</span>
      </div>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
    </div>
  )
}

type ActivityCardProps<T> = {
  title: string
  icon: ReactNode
  count: number
  windowLabel: string
  items: T[]
  renderItem: (item: T) => ReactNode
  itemDate: (item: T) => string
  emptyLabel: string
}

function ActivityCard<T>({
  title,
  icon,
  count,
  windowLabel,
  items,
  renderItem,
  itemDate,
  emptyLabel,
}: ActivityCardProps<T>) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {icon}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-baseline gap-2">
          <p className="text-2xl font-semibold tabular-nums">{count.toLocaleString()}</p>
          <p className="text-sm text-muted-foreground">{windowLabel}</p>
        </div>
        {items.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">{emptyLabel}</p>
        ) : (
          <ul className="mt-4 space-y-2">
            {items.map((item, idx) => (
              <li key={idx} className="flex items-baseline justify-between gap-3 text-sm">
                <span className="min-w-0 truncate">{renderItem(item)}</span>
                <span className="shrink-0 text-xs text-muted-foreground">{formatRelativeDate(itemDate(item))}</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

function PlaceholderCard({ title, icon, body }: { title: string; icon: ReactNode; body: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {icon}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">{body}</p>
      </CardContent>
    </Card>
  )
}

export default function DashboardHome({
  greeting_name,
  stats,
  new_donors,
  new_followers,
}: DashboardHomeProps) {
  return (
    <>
      <Head title="Dashboard" />
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10">
        <h1 className="text-2xl font-semibold tracking-tight">
          Hi {greeting_name ?? 'there'}
        </h1>

        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard
            label="Published"
            value={stats.published_campaigns}
            icon={<Megaphone className="size-4" />}
          />
          <StatCard
            label="Drafts"
            value={stats.draft_campaigns}
            icon={<FileText className="size-4" />}
          />
          <StatCard
            label="Total Raised"
            value={formatMoney(stats.total_raised_cents)}
            icon={<DollarSign className="size-4" />}
          />
          <StatCard
            label="Supporters"
            value={stats.total_supporters.toLocaleString()}
            icon={<Users className="size-4" />}
          />
        </div>

        <div className="mt-8 grid gap-6 sm:grid-cols-2">
          <ActivityCard
            title="New donors"
            icon={<Heart className="size-4 text-primary" />}
            count={new_donors.count}
            windowLabel="last 30 days"
            items={new_donors.recent}
            renderItem={(d) => (
              <span>
                <span className="font-medium">{d.name ?? 'Anonymous'}</span>
                <span className="text-muted-foreground"> · {formatMoney(d.amount_cents)}</span>
                {d.event_title && (
                  <span className="text-muted-foreground"> · {d.event_title}</span>
                )}
              </span>
            )}
            itemDate={(d) => d.created_at}
            emptyLabel="No new donors yet"
          />
          <ActivityCard
            title="New followers"
            icon={<UserPlus className="size-4 text-primary" />}
            count={new_followers.count}
            windowLabel="last 30 days"
            items={new_followers.recent}
            renderItem={(f) => <span className="font-medium">{f.name}</span>}
            itemDate={(f) => f.created_at}
            emptyLabel="No new followers yet"
          />
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-3">
          <PlaceholderCard
            title="FAQ"
            icon={<HelpCircle className="size-4 text-primary" />}
            body="Common questions for organizers, coming soon."
          />
          <PlaceholderCard
            title="Announcements"
            icon={<Bell className="size-4 text-primary" />}
            body="Product updates from the Gala Season team will appear here."
          />
          <PlaceholderCard
            title="New features"
            icon={<Sparkles className="size-4 text-primary" />}
            body="Recent additions you might have missed."
          />
        </div>
      </div>
    </>
  )
}

DashboardHome.layout = (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>
